/**
 * Social Feed section
 * ---------------------------------------------------------------------------
 * Drives the four behaviours the markup only declares:
 *   1. Lazy tile backgrounds  — data-sf-image is only promoted to a real
 *      background-image once the tile scrolls near the viewport, so a section
 *      far down the page doesn't eager-load a dozen full-res photos.
 *   2. Auto-spotlight         — one tile at a time gets .is-active (the big
 *      centre cell) on a rotating interval, paused while off-screen or while
 *      the modal is open.
 *   3. Like-count ticker      — the featured tile's like number drifts upward
 *      with a bounce, so the wall reads as live.
 *   4. Post modal             — a single dialog per section, relocated to
 *      <body> on init so it escapes any transformed/overflow ancestor.
 *      Video posts (data-sf-video) play inline in the modal; photo posts
 *      show the still image.
 *
 * Optionally replaces the hand-authored tiles with posts fetched from a feed
 * JSON endpoint (section setting "Feed URL").
 */
(function () {
  'use strict';

  var SELECTOR = '[data-social-feed]';

  /* ------------------------------------------------------------------ utils */

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // "12.4K" -> 12400, "1.2M" -> 1200000, "3,481" -> 3481
  function parseCount(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number') return raw;
    var text = String(raw).trim().replace(/,/g, '');
    var match = text.match(/^([\d.]+)\s*([kKmM]?)/);
    if (!match) return null;
    var value = parseFloat(match[1]);
    if (isNaN(value)) return null;
    var suffix = match[2].toUpperCase();
    if (suffix === 'K') value *= 1000;
    if (suffix === 'M') value *= 1000000;
    return Math.round(value);
  }

  function hasSuffix(raw) {
    return /[kKmM]\s*$/.test(String(raw || '').trim());
  }

  function formatCount(value, compact) {
    if (!compact) return Number(value).toLocaleString();
    if (value >= 1000000) return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (value >= 1000) return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(value);
  }

  // First non-empty value among several candidate keys. Feed services disagree
  // on snake_case vs camelCase, so we check both spellings of everything.
  function pick(obj, keys) {
    for (var i = 0; i < keys.length; i++) {
      var value = obj[keys[i]];
      if (value != null && value !== '') return value;
    }
    return null;
  }

  // Some APIs nest what we want one level down, e.g. caption: { text: "..." }
  // or media: { url: "..." }.
  function unwrap(value) {
    if (value == null) return null;
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (typeof value === 'object') return pick(value, ['url', 'src', 'text', 'value', 'mediaUrl', 'media_url']);
    return null;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ----------------------------------------------------------------- section */

  function SocialFeed(root) {
    if (root.__socialFeed) root.__socialFeed.destroy();
    root.__socialFeed = this;

    this.root = root;
    this.grid = root.querySelector('[data-sf-grid]');
    this.modal = root.querySelector('[data-sf-modal]');
    this.abort = new AbortController();
    this.signal = this.abort.signal;

    this.rotateMs = parseInt(root.dataset.sfRotate, 10) || 0;
    this.feedUrl = (root.dataset.sfFeedUrl || '').trim();
    this.feedCount = parseInt(root.dataset.sfFeedCount, 10) || 13;
    this.network = root.dataset.sfNetwork || 'Instagram';
    this.captionFallback = root.dataset.sfCaptionFallback || '';

    this.items = [];
    this.activeIndex = -1;
    this.rotateTimer = null;
    this.inView = false;
    this.modalOpen = false;
    this.lastFocus = null;

    this.init();
  }

  SocialFeed.prototype.init = function () {
    if (!this.grid) return;

    this.setupModal();
    this.collectItems();
    this.observeImages();
    this.observeViewport();
    this.startRotation();

    if (this.feedUrl) this.loadFeed();
  };

  /* -------------------------------------------------------------- tile setup */

  SocialFeed.prototype.collectItems = function () {
    var self = this;
    this.items = Array.prototype.slice.call(this.grid.querySelectorAll('[data-sf-open]'));

    this.items.forEach(function (item) {
      if (item.__sfBound) return;
      item.__sfBound = true;
      item.addEventListener(
        'click',
        function (event) {
          // The tile is an <a> so it still works without JS / on middle-click.
          // With JS we intercept and open the modal instead; the modal's CTA
          // is what actually sends people out to the post.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
          event.preventDefault();
          self.openModal(item);
        },
        { signal: self.signal }
      );
    });
  };

  SocialFeed.prototype.observeImages = function () {
    var self = this;

    function paint(item) {
      var url = item.dataset.sfImage;
      if (!url || item.__sfPainted) return;
      item.__sfPainted = true;
      item.style.setProperty('--social-feed-image', 'url("' + url.replace(/"/g, '%22') + '")');
    }

    if (!('IntersectionObserver' in window)) {
      this.items.forEach(paint);
      return;
    }

    if (this.imageObserver) this.imageObserver.disconnect();
    this.imageObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          paint(entry.target);
          self.imageObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '400px 0px' }
    );

    this.items.forEach(function (item) {
      self.imageObserver.observe(item);
    });
  };

  /* --------------------------------------------------------------- spotlight */

  SocialFeed.prototype.observeViewport = function () {
    var self = this;

    if (!('IntersectionObserver' in window)) {
      this.inView = true;
      return;
    }

    if (this.viewObserver) this.viewObserver.disconnect();
    this.viewObserver = new IntersectionObserver(
      function (entries) {
        self.inView = entries[0].isIntersecting;
        if (self.inView) self.startRotation();
        else self.stopRotation();
      },
      { threshold: 0.05 }
    );
    this.viewObserver.observe(this.grid);
  };

  SocialFeed.prototype.setActive = function (index) {
    if (!this.items.length) return;
    var next = ((index % this.items.length) + this.items.length) % this.items.length;

    this.items.forEach(function (item) {
      item.classList.remove('is-active');
    });

    this.activeIndex = next;
    var active = this.items[next];
    active.classList.add('is-active');
    this.tickLikes(active);
  };

  SocialFeed.prototype.startRotation = function () {
    if (this.activeIndex === -1) this.setActive(0);
    if (this.rotateTimer || !this.rotateMs || this.modalOpen) return;
    if (!this.inView && 'IntersectionObserver' in window) return;

    var self = this;
    this.rotateTimer = window.setInterval(function () {
      self.setActive(self.activeIndex + 1);
    }, this.rotateMs);
  };

  SocialFeed.prototype.stopRotation = function () {
    if (!this.rotateTimer) return;
    window.clearInterval(this.rotateTimer);
    this.rotateTimer = null;
  };

  /* ------------------------------------------------------------- like ticker */

  SocialFeed.prototype.tickLikes = function (item) {
    var wrap = item.querySelector('.social-feed__likes');
    var num = item.querySelector('[data-sf-likes-num]');
    if (!wrap || !num) return;

    var current = parseCount(num.textContent);
    if (current == null) return;

    var compact = hasSuffix(num.textContent) || current >= 10000;
    var bumped = current + Math.floor(Math.random() * 4) + 1;
    num.textContent = formatCount(bumped, compact);
    item.dataset.sfLikes = String(bumped);

    if (prefersReducedMotion()) return;
    wrap.classList.remove('is-bounce');
    void wrap.offsetWidth; // restart the animation
    wrap.classList.add('is-bounce');
  };

  /* -------------------------------------------------------------------- modal */

  SocialFeed.prototype.setupModal = function () {
    if (!this.modal) return;
    var self = this;

    // Escape any transformed / overflow-hidden ancestor so position:fixed and
    // the backdrop actually cover the viewport.
    document.body.appendChild(this.modal);

    this.modalParts = {
      img: this.modal.querySelector('[data-sf-modal-img]'),
      video: this.modal.querySelector('[data-sf-modal-video]'),
      likes: this.modal.querySelector('[data-sf-modal-likes]'),
      likesWrap: this.modal.querySelector('[data-sf-modal-likes-wrap]'),
      comments: this.modal.querySelector('[data-sf-modal-comments]'),
      commentsWrap: this.modal.querySelector('[data-sf-modal-comments-wrap]'),
      views: this.modal.querySelector('[data-sf-modal-views]'),
      viewsWrap: this.modal.querySelector('[data-sf-modal-views-wrap]'),
      caption: this.modal.querySelector('[data-sf-modal-caption]'),
      link: this.modal.querySelector('[data-sf-modal-link]'),
      close: this.modal.querySelector('.social-feed-modal__close')
    };

    this.modal.querySelectorAll('[data-sf-close]').forEach(function (el) {
      el.addEventListener('click', function () { self.closeModal(); }, { signal: self.signal });
    });

    document.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'Escape' && self.modalOpen) self.closeModal();
      },
      { signal: this.signal }
    );
  };

  SocialFeed.prototype.resetModalVideo = function () {
    var video = this.modalParts && this.modalParts.video;
    if (!video) return;
    video.pause();
    video.removeAttribute('src');
    video.load(); // fully release the stream
    video.style.display = 'none';
  };

  SocialFeed.prototype.openModal = function (item) {
    if (!this.modal) {
      if (item.dataset.sfLink) window.open(item.dataset.sfLink, '_blank', 'noopener');
      return;
    }

    var p = this.modalParts;
    var data = item.dataset;
    var videoSrc = (data.sfVideo || '').trim();

    // Video posts play inline; photo posts show the still.
    if (p.video && videoSrc) {
      if (p.img) p.img.style.display = 'none';
      p.video.src = videoSrc;
      p.video.poster = data.sfImage || '';
      p.video.style.display = 'block';
    } else {
      this.resetModalVideo();
      if (p.img) {
        p.img.src = data.sfImage || '';
        p.img.alt = data.sfCaption || this.captionFallback;
        p.img.style.display = 'block';
      }
    }

    this.fillStat(p.likesWrap, p.likes, data.sfLikes);
    this.fillStat(p.commentsWrap, p.comments, data.sfComments);
    this.fillStat(p.viewsWrap, p.views, data.sfViews);

    if (p.caption) p.caption.textContent = data.sfCaption || this.captionFallback;

    if (p.link) {
      if (data.sfLink) {
        p.link.href = data.sfLink;
        p.link.hidden = false;
      } else {
        p.link.hidden = true;
      }
    }

    this.lastFocus = document.activeElement;
    this.modalOpen = true;
    this.stopRotation();

    this.modal.hidden = false;
    // Next frame so the opacity/transform transition actually runs.
    var modal = this.modal;
    window.requestAnimationFrame(function () { modal.classList.add('is-open'); });
    document.body.classList.add('sf-modal-open');
    if (p.close) p.close.focus();

    // Autoplay the video once the open transition has settled. Muted-less
    // autoplay can be blocked by the browser; the catch leaves the native
    // controls in place so the visitor just presses play.
    if (p.video && videoSrc && !prefersReducedMotion()) {
      var vid = p.video;
      window.setTimeout(function () {
        vid.play().catch(function () {});
      }, 280);
    }
  };

  SocialFeed.prototype.fillStat = function (wrap, target, value) {
    if (!wrap || !target) return;
    var parsed = parseCount(value);
    if (parsed == null) {
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;
    target.textContent = formatCount(parsed, parsed >= 10000);
  };

  SocialFeed.prototype.closeModal = function () {
    if (!this.modal || !this.modalOpen) return;
    var self = this;

    this.modalOpen = false;
    this.resetModalVideo();
    this.modal.classList.remove('is-open');
    document.body.classList.remove('sf-modal-open');

    window.setTimeout(function () {
      if (self.modalOpen) return;
      self.modal.hidden = true;
      if (self.modalParts && self.modalParts.img) self.modalParts.img.src = '';
    }, 250);

    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    this.startRotation();
  };

  /* ---------------------------------------------------------------- live feed */

  SocialFeed.prototype.loadFeed = function () {
    var self = this;

    fetch(this.feedUrl, { signal: this.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Feed responded ' + response.status);
        return response.json();
      })
      .then(function (json) {
        var posts = self.normalizeFeed(json).slice(0, self.feedCount);
        if (!posts.length) return; // keep the hand-authored tiles as the fallback
        self.renderFeed(posts);
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        console.warn('[social-feed] feed unavailable, using manual posts:', error.message);
      });
  };

  // Tolerant of the common feed-service shapes in both snake_case and camelCase.
  // If your service uses keys not listed here, add them to the relevant array
  // rather than reshaping the JSON upstream.
  SocialFeed.prototype.normalizeFeed = function (json) {
    var list = Array.isArray(json)
      ? json
      : json.posts || json.data || json.items || json.media || json.results || [];
    if (!Array.isArray(list)) return [];

    return list.map(function (post) {
      var type = String(pick(post, ['media_type', 'mediaType', 'type']) || '').toLowerCase();
      var isVideo =
        post.is_video === true ||
        post.isVideo === true ||
        type.indexOf('video') !== -1 ||
        type.indexOf('reel') !== -1;

      // For a video, media_url is the .mp4 — we want the poster frame instead.
      // For a photo, the thumbnail is often a downscaled crop, so prefer the full one.
      var full = unwrap(pick(post, ['media_url', 'mediaUrl', 'media', 'image', 'image_url', 'imageUrl', 'display_url', 'displayUrl', 'src']));
      var thumb = unwrap(pick(post, ['thumbnail_url', 'thumbnailUrl', 'thumbnail', 'poster', 'preview']));

      return {
        image: (isVideo ? thumb || full : full || thumb) || '',
        link: unwrap(pick(post, ['permalink', 'link', 'url', 'post_url', 'postUrl'])) || '',
        caption: unwrap(pick(post, ['caption', 'prunedCaption', 'text', 'title', 'description'])) || '',
        likes: pick(post, ['like_count', 'likeCount', 'likes', 'likesCount']),
        comments: pick(post, ['comments_count', 'commentsCount', 'comment_count', 'commentCount', 'comments']),
        views: pick(post, ['view_count', 'viewCount', 'views', 'play_count', 'playCount', 'video_views']),
        video: isVideo,
        // For video posts the media_url IS the playable file — carry it through
        // so the lightbox can play it. Explicit video-url keys win if present.
        videoUrl: isVideo
          ? unwrap(pick(post, ['video_url', 'videoUrl', 'video_src', 'videoSrc', 'media_url', 'mediaUrl'])) || ''
          : '',
        duration: pick(post, ['duration', 'video_duration', 'videoDuration']) || ''
      };
    }).filter(function (post) {
      return post.image;
    });
  };

  SocialFeed.prototype.renderFeed = function (posts) {
    var self = this;
    var network = esc(this.network);

    var html = posts.map(function (post) {
      var caption = post.caption || self.captionFallback;
      var tag = post.link ? 'a' : 'button';
      var open =
        '<' + tag +
        (post.link ? ' href="' + esc(post.link) + '" target="_blank" rel="noopener"' : ' type="button"') +
        ' class="social-feed__item social-feed__item--wide-square"' +
        ' data-sf-open' +
        ' data-sf-image="' + esc(post.image) + '"' +
        ' data-sf-likes="' + esc(post.likes) + '"' +
        ' data-sf-views="' + esc(post.views) + '"' +
        ' data-sf-comments="' + esc(post.comments) + '"' +
        ' data-sf-caption="' + esc(caption) + '"' +
        ' data-sf-link="' + esc(post.link) + '"' +
        ' data-sf-video="' + esc(post.videoUrl) + '">';

      var likes = '';
      if (post.likes != null && post.likes !== '') {
        likes =
          '<span class="social-feed__likes" title="' + esc(post.likes) + ' likes on ' + network + '" aria-hidden="true">' +
          '<span class="social-feed__likes-num" data-sf-likes-num="' + esc(post.likes) + '">' + esc(post.likes) + '</span> Likes' +
          '</span>';
      }

      var badge =
        '<span class="social-feed__badge" title="View on ' + network + '" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">' +
        '<rect x="3" y="3" width="18" height="18" rx="5"></rect>' +
        '<circle cx="12" cy="12" r="4"></circle>' +
        '<circle cx="17.3" cy="6.7" r="1" fill="currentColor"></circle>' +
        '</svg></span>';

      var play = '';
      if (post.video) {
        play =
          '<span class="social-feed__play" aria-hidden="true">' +
          '<span class="social-feed__play-circle"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"></path></svg></span>' +
          (post.duration ? '<span class="social-feed__video-meta">' + esc(post.duration) + '</span>' : '') +
          '</span>';
      }

      var captionEl = caption
        ? '<span class="social-feed__caption"><span>' + esc(caption) + '</span></span>'
        : '';

      return open + likes + badge + play + captionEl + '<span class="social-feed__timer" aria-hidden="true"></span></' + tag + '>';
    }).join('');

    this.stopRotation();
    this.activeIndex = -1;
    this.grid.innerHTML = html;

    this.collectItems();
    this.observeImages();
    this.startRotation();
  };

  /* ------------------------------------------------------------------ teardown */

  SocialFeed.prototype.destroy = function () {
    this.stopRotation();
    this.resetModalVideo();
    if (this.imageObserver) this.imageObserver.disconnect();
    if (this.viewObserver) this.viewObserver.disconnect();
    if (this.modal && this.modal.parentNode === document.body) {
      document.body.removeChild(this.modal);
    }
    document.body.classList.remove('sf-modal-open');
    this.abort.abort();
    this.root.__socialFeed = null;
  };

  /* ---------------------------------------------------------------- bootstrap */

  function initAll(scope) {
    var container = scope || document;
    var roots = container.matches && container.matches(SELECTOR)
      ? [container]
      : Array.prototype.slice.call(container.querySelectorAll(SELECTOR));
    roots.forEach(function (root) { new SocialFeed(root); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: rebuild on section reload, tear down on removal.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector ? event.target.querySelector(SELECTOR) : null;
    if (root && root.__socialFeed) root.__socialFeed.destroy();
  });
})();